/* eslint-disable jsx-a11y/no-static-element-interactions */

// ported from https://github.com/adrsch/slider-captcha/blob/master/react/src/challenge.js
import React, { useState, ReactElement, useEffect, useRef } from 'react'
import ArrowIcon from 'images/ui/chevron-right.svg'
import SuccessIcon from 'images/captcha/success-icon.svg'
import FailureIcon from 'images/captcha/failure-icon.svg'
import { CaptchaImages, CaptchaTrail } from './Captcha.interface'
import './dark.scss'
import { Svg } from 'ui/Svg'

const imageDataUrl = image => `data:image/png;base64,${image}`

const slider = {
  default: {
    track: 'scaptcha-card-slider-track-default',
    control: 'scaptcha-card-slider-control-default',
    icon: <Svg svg={ArrowIcon} fill={'aliceBlue60'} descriptiveText="Slider arrow icon" />,
  },
  active: {
    track: 'scaptcha-card-slider-track-active',
    control: 'scaptcha-card-slider-control-active',
    icon: <Svg svg={ArrowIcon} descriptiveText="Slider arrow icon" />,
  },
  success: {
    track: 'scaptcha-card-slider-track-success',
    control: 'scaptcha-card-slider-control-success',
    icon: <SuccessIcon />,
  },
  failure: {
    track: 'scaptcha-card-slider-track-failure',
    control: 'scaptcha-card-slider-control-failure',
    icon: <FailureIcon />,
  },
}

interface ChallengeProps {
  text: string
  captcha: CaptchaImages
  completeCaptcha: (response: number, trail: CaptchaTrail) => Promise<boolean>
}
export default function Challenge({
  text,
  captcha,
  completeCaptcha,
}: ChallengeProps): ReactElement {
  const captchaRef = useRef<HTMLInputElement>(null)
  const [sliderVariant, setSliderVariant] = useState(slider.default)
  const [solving, setSolving] = useState(false)
  const [submittedResponse, setSubmittedResponse] = useState(false)
  const [origin, setOrigin] = useState({ x: 0, y: 0 })
  const [trail, setTrail] = useState({ x: [0], y: [0] })

  useEffect(() => {
    setOrigin({ x: 0, y: 0 })
    setTrail({ x: [0], y: [0] })
    setSubmittedResponse(false)
    setSolving(false)
    setSliderVariant(slider.default)
    captchaRef.current?.scrollIntoView({ block: 'center' })
    captchaRef.current?.focus()
  }, [captcha])

  // Converts distances along the control track to corresponding distances moved by the puzzle piece
  const scaleSliderPosition = (x: number) => 0.86 * x

  const handleStart = e => {
    if (submittedResponse) {
      return
    }
    setOrigin({
      x: e.clientX || e.touches[0].clientX,
      y: e.clientY || e.touches[0].clientY,
    })
    setSolving(true)
    setSliderVariant(slider.active)
  }

  const handleMove = e => {
    if (!solving || submittedResponse) {
      return
    }

    const move = {
      x: (e.clientX || e.touches[0].clientX) - origin.x,
      y: (e.clientY || e.touches[0].clientY) - origin.y,
    }
    if (move.x > 270 || move.x < 0) {
      return
    } // Don't update if outside bounds of captcha
    setTrail({
      x: trail.x.concat([move.x]),
      y: trail.y.concat([move.y]),
    })
  }

  const handleEnd = () => {
    if (!solving || submittedResponse) {
      return
    }
    setSubmittedResponse(true)
    completeCaptcha(scaleSliderPosition(trail.x[trail.x.length - 1]), trail).then(validated => {
      setSliderVariant(validated ? slider.success : slider.failure)
    })
  }

  const handleEnter = () => {
    if (solving || submittedResponse) {
      return
    }
    setSliderVariant(slider.active)
  }

  const handleLeave = () => {
    if (solving) {
      return
    }
    setSliderVariant(slider.default)
  }

  return (
    <div
      ref={captchaRef}
      className="scaptcha-card-element ios-scrolling-glitches-fix"
      style={{ position: 'relative' }}
      draggable="false"
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
    >
      <div
        className="scaptcha-card-background scaptcha-card-element"
        style={{
          backgroundImage: `url('${imageDataUrl(captcha.background)}')`,
        }}
      />
      <div
        className="scaptcha-card-slider-puzzle scaptcha-card-element"
        style={{
          top: captcha.top,
          backgroundImage: `url('${imageDataUrl(captcha.slider)}')`,
          left: `${scaleSliderPosition(trail.x[trail.x.length - 1])}px`,
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      />
      <div className="scaptcha-card-slider-container scaptcha-card-element">
        <div className="scaptcha-card-slider-track scaptcha-card-element" />
        <div
          className="scaptcha-card-slider-label scaptcha-card-element"
          style={{ opacity: solving ? 0 : 1 }}
        >
          <span>{text}</span>
        </div>
        <div
          className={`scaptcha-card-slider-mask ${sliderVariant.track} scaptcha-card-element`}
          style={{ width: `${trail.x[trail.x.length - 1] + 30}px` }}
        />
        <div className="scaptcha-card-slider-container scaptcha-card-element" draggable="false" />
        <div
          className={`scaptcha-card-slider-control ${sliderVariant.control} scaptcha-card-element`}
          style={{ left: `${trail.x[trail.x.length - 1]}px` }}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {sliderVariant.icon}
        </div>
      </div>
    </div>
  )
}
